import React from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { SESSIONS, stageOf, isIntakeComplete } from "@/data/bridgeData";
import { useEntries } from "@/context/EntriesContext";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, Circle, CheckCircle2, Lock } from "lucide-react";

export default function Sessions() {
  const navigate = useNavigate();
  const { getEntry, loading } = useEntries();
  const { user } = useAuth();
  const locked = !loading && user?.role === "participant" && !isIntakeComplete(getEntry);

  return (
    <Layout>
      <div className="label-eyebrow mb-3">Six live sessions</div>
      <h1 className="font-serif text-4xl sm:text-5xl font-light text-stone-900 mb-3">The session arc</h1>
      <p className="max-w-2xl text-stone-600 leading-relaxed mb-6">
        Each session has a coach-ready agenda, reflection exercises, an AI prompt library, and between-session work.
        Open a workspace to do the deep work and synthesize with your assistant.
      </p>

      {locked && (
        <div data-testid="sessions-locked-banner" className="mb-8 flex flex-wrap items-center justify-between gap-4 rounded-sm border border-brand-accent/40 bg-brand-parchment/50 p-5">
          <div className="flex items-start gap-3">
            <Lock size={18} className="mt-0.5 text-brand-primary shrink-0" />
            <div>
              <div className="font-serif text-xl text-stone-900">Complete your intake to begin</div>
              <p className="text-sm text-stone-600">Sessions unlock once you've recorded where you're starting from and your baseline.</p>
            </div>
          </div>
          <button
            data-testid="sessions-go-intake"
            onClick={() => navigate("/intake")}
            className="inline-flex items-center gap-2 rounded-sm bg-brand-primary px-4 py-2.5 text-white transition-all duration-300 hover:bg-brand-hover"
          >
            Go to intake <ArrowRight size={15} />
          </button>
        </div>
      )}

      <div className="space-y-4">
        {SESSIONS.map((s) => {
          const stage = stageOf(s.stage);
          const msDone = getEntry(`milestone:${s.milestone.n}:done`, false);
          let statusIcon = <Circle size={18} className="text-stone-300" />;
          if (locked) statusIcon = <Lock size={16} className="text-stone-400" />;
          else if (msDone) statusIcon = <CheckCircle2 size={18} className="text-brand-sage" />;
          return (
            <button
              key={s.id}
              data-testid={`session-card-${s.id}`}
              onClick={() => navigate(locked ? "/intake" : `/sessions/${s.id}`)}
              className={`group flex w-full items-center gap-6 rounded-sm border border-stone-200 bg-white p-6 text-left transition-all duration-300 hover:-translate-y-0.5 hover:border-brand-primary ${locked ? "opacity-60" : ""}`}
            >
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-stone-200 font-serif text-2xl text-stone-700">
                {s.id}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-1">
                  <span className="label-eyebrow">Week {s.week}</span>
                  <span className="inline-flex items-center gap-1.5 text-xs text-stone-500">
                    <span className="h-2 w-2 rounded-full" style={{ background: stage.color }} /> {stage.name}
                  </span>
                </div>
                <h3 className="font-serif text-2xl text-stone-900">{s.title}</h3>
                <p className="mt-1 text-sm text-stone-500 line-clamp-1">{s.purpose}</p>
              </div>
              <div className="hidden sm:flex items-center gap-2 text-sm text-stone-500">
                {statusIcon}
                <span>{locked ? "Locked" : `Milestone ${s.milestone.n}`}</span>
              </div>
              <ArrowRight size={18} className="text-stone-300 transition-all duration-300 group-hover:text-brand-primary group-hover:translate-x-1" />
            </button>
          );
        })}
      </div>
    </Layout>
  );
}
